'use client';

import { createElement, JSX } from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import mlCtaCard from './ml-cta-card.variants';

// Components
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';

// Types
import { ETextSize } from '@/components/atoms/at-text/at-text.types';
import {
  EClickableSize,
  EClickableTarget,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EThemes } from '@/lib/contentstack-sdk/types';
import { EImageIntents } from '@/components/atoms/at-media/at-media.types';
import { IMlCtaCardProps } from './ml-cta-card.types';

// Hooks + Utils
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { sendGTMNavigationEvent } from '@/components/helpers/analytics/gtm';

/**
 * `MlCtaCard` is a flexible promotional card component that supports background imagery,
 * theming, optional icons, and an optional CTA. It is commonly used in hero sections,
 * feature callouts, and promotional grids.
 *
 * - If `showCta` is true, a CTA button will render below the content.
 * - If `showCta` is false and a `link` is provided, the title becomes a clickable link.
 * - Supports `AtMedia` background images pulled from a responsive CMS image object.
 */
export const MlCtaCard = ({
  theme = EThemes.DARK,
  title,
  description = '',
  showCta = true,
  headingElement = 'h2',
  headingVariant = 'h1',
  headingAlt = false,
  icon,
  size = EClickableSize.LG,
  bgImage,
  link,
  className,
  element = 'article',
  interactLocation,
  ariaLabel = 'Article, Schumacher Homes',
  showOverlay = false,
}: IMlCtaCardProps): JSX.Element => {
  const { base, body } = mlCtaCard({ size, showCta });

  let imageProps = null;

  if (
    bgImage &&
    (bgImage.mobile_image_url ||
      bgImage.tablet_image_url ||
      bgImage.desktop_image_url)
  ) {
    imageProps = parseImageFromCMS(bgImage);
  }

  const children = (
    <>
      {imageProps && (
        <AtMedia
          intent={EImageIntents.BACKGROUND}
          className="absolute h-full w-full"
          showOverlay={showOverlay}
          sizes="(max-width: 768px) 100vw, 75vw"
          {...imageProps}
        />
      )}

      <span className={body()}>
        {icon && (
          <span className="text-32 lg:text-40">
            <AtIcon icon={icon} color="var(--color-button-outline-tertiary)" />
          </span>
        )}

        {title &&
          (!showCta && link && link.url.href ? (
            <Link
              href={link.url.href}
              target={
                EClickableTarget[link.target as keyof typeof EClickableTarget]
              }
              onClick={() =>
                sendGTMNavigationEvent(
                  link?.url?.title || title || ariaLabel,
                  interactLocation || 'ml-cta-card',
                  link.url.href,
                )
              }
              className="expandCoverage outline-none focus:after:outline focus:after:outline-2 focus:after:outline-offset-4 focus:after:outline-[var(--color-ui-stroke-tertiary)]"
            >
              <AtHeader
                element={headingElement}
                variant={headingVariant}
                alt={headingAlt}
                className="max-w-775 text-balance"
              >
                {title}
              </AtHeader>
            </Link>
          ) : (
            <AtHeader
              element={headingElement}
              variant={headingVariant}
              alt={headingAlt}
              className="max-w-775 text-balance"
            >
              {title}
            </AtHeader>
          ))}

        {description && (
          <AtText
            size={ETextSize.LG}
            className="max-w-570 text-balance text-skin-tertiary"
          >
            {description}
          </AtText>
        )}
      </span>
      {showCta && link && (
        <AtClickable
          href={link.url.href}
          ariaLabel={link.aria_label}
          outline={link.outline}
          variant={link.variant as EClickableVariants}
          type={link.type}
          target={
            EClickableTarget[link.target as keyof typeof EClickableTarget]
          }
          label={link.url.title}
          className="z-10 mt-48 block w-fit"
          interactLocation="ml-cta-card"
        />
      )}
    </>
  );

  return createElement(
    element,
    {
      'data-theme': theme,
      'aria-label': ariaLabel,
      className: classNames(base(), className),
    },
    children,
  );
};
